// Force deployment - password functionality
import React, { useState, useEffect } from 'react';
import { useTheme } from '@/state/theme/ThemeProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import api from '@/services/api';
// import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useSearchParams } from 'react-router-dom';
import { TenantSubscriptionPage } from '@/pages/admin/TenantSubscriptionPage';
import { PreferencesTab } from './profile/PreferencesTab';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  title?: string;
  primaryPositionId?: string;
  status: string;
  isTenantAdmin: boolean;
  emailConfirmed: boolean;
  preferences: Record<string, string>;
  tenantName: string;
  tenantDomain: string;
  createdAt: string;
  updatedAt: string;
  profileImageUrl?: string;
  // Hierarchy information
  businessHierarchy?: {
    id: string;
    name: string;
  };
  functionalHierarchy?: {
    id: string;
    name: string;
  };
  geographicHierarchy?: {
    id: string;
    name: string;
  };
  // User roles
  roles?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  clearance?: string;
  hasTenantRootMembership?: boolean;
}

export const UserProfilePage: React.FC = () => {
  const { setTheme, loadThemeFromPreferences, availableThemes } = useTheme();
  const { setLanguage, loadLanguageFromPreferences, availableLanguages, t } = useLanguage();
  const { user, setUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Profile picture upload state
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pictureError, setPictureError] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Hierarchy editing state
  const [businessHierarchyOptions, setBusinessHierarchyOptions] = useState<Array<{id: string, name: string}>>([]);
  const [functionalHierarchyOptions, setFunctionalHierarchyOptions] = useState<Array<{id: string, name: string}>>([]);
  const [geographicHierarchyOptions, setGeographicHierarchyOptions] = useState<Array<{id: string, name: string}>>([]);
  const [hierarchyFormData, setHierarchyFormData] = useState({
    business_hierarchy_id: '',
    functional_hierarchy_id: '',
    geographic_hierarchy_id: ''
  });
  const [savingHierarchies, setSavingHierarchies] = useState(false);
  const [hierarchySaveTimeout, setHierarchySaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Position (replaces title) - tenant-scoped, typable dropdown
  const [positionOptions, setPositionOptions] = useState<Array<{ id: string; position_name: string; position_code?: string }>>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<string>('');
  const [positionSearch, setPositionSearch] = useState('');
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const [savingPosition, setSavingPosition] = useState(false);
  const positionDropdownRef = React.useRef<HTMLDivElement>(null);

  const activeSection = (() => {
    const section = (searchParams.get('section') || 'personal').toLowerCase();
    if (section === 'business' || section === 'subscription' || section === 'preferences') {
      return section;
    }
    return 'personal';
  })() as 'personal' | 'business' | 'subscription' | 'preferences';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (positionDropdownRef.current && !positionDropdownRef.current.contains(e.target as Node)) {
        setPositionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    themePreference: 'tmos-light',
    language: 'en-US',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    notifications: {
      email: true,
      push: false,
      sms: false,
      inApp: true
    },
    dashboardLayout: 'grid',
    compactMode: false,
    autoSave: true,
    accessibility: {
      highContrast: false,
      fontSize: 'medium',
      screenReader: false
    },
    privacy: {
      profileVisibility: 'team',
      activityStatus: 'visible',
      dataSharing: 'minimal'
    }
  });

  useEffect(() => {
    fetchUserProfile();
    
    // Cleanup timeout on unmount
    return () => {
      if (hierarchySaveTimeout) {
        clearTimeout(hierarchySaveTimeout);
      }
    };
  }, []);

  // Load hierarchies for the user's tenant
  useEffect(() => {
    if (user?.tenantId) {
      loadHierarchies(user.tenantId);
    }
  }, [user?.tenantId]);

  // Load hierarchy options for dropdowns
  const loadHierarchies = async (tenantId: string) => {
    try {
      const tenantParam = `?tenant_id=${tenantId}`;
      
      // Load all hierarchies in parallel
      const [businessRes, functionalRes, geographicRes] = await Promise.all([
        api.get(`/hierarchy/business${tenantParam}`).catch(() => ({ data: { data: [] } })),
        api.get(`/hierarchy/functional${tenantParam}`).catch(() => ({ data: { data: [] } })),
        api.get(`/hierarchy/geographic${tenantParam}`).catch(() => ({ data: { data: [] } }))
      ]);

      // API returns flat array, not tree structure - convert to dropdown options
      const convertToDropdownOptions = (items: any[]): Array<{id: string, name: string}> => {
        if (!Array.isArray(items)) return [];
        return items.map(item => ({
          id: item.id,
          name: item.name || item.display_name || item.label || 'Unnamed'
        }));
      };

      const businessData = businessRes.data?.data || businessRes.data || [];
      const functionalData = functionalRes.data?.data || functionalRes.data || [];
      const geographicData = geographicRes.data?.data || geographicRes.data || [];

      setBusinessHierarchyOptions(convertToDropdownOptions(businessData));
      setFunctionalHierarchyOptions(convertToDropdownOptions(functionalData));
      setGeographicHierarchyOptions(convertToDropdownOptions(geographicData));
    } catch (error) {
      console.error('Error loading hierarchies:', error);
    }
  };

  // Save hierarchy assignments
  const saveHierarchies = async () => {
    if (!profile) return;

    try {
      setSavingHierarchies(true);
      setError('');

      await api.post(`/users/hierarchy/assignments/${profile.id}`, {
        business_hierarchy_id: hierarchyFormData.business_hierarchy_id || null,
        functional_hierarchy_id: hierarchyFormData.functional_hierarchy_id || null,
        geographic_hierarchy_id: hierarchyFormData.geographic_hierarchy_id || null
      });

      // Reload profile to get updated hierarchy names
      await fetchUserProfile();
      
      setError('');
    } catch (error: any) {
      console.error('Error saving hierarchies:', error);
      setError(`Failed to save hierarchies: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    } finally {
      setSavingHierarchies(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/users/profile');
      const userProfile = response.data.data;
      
      // Fetch hierarchy information
      try {
                        const hierarchyResponse = await api.get(`/users/hierarchy/assignments/${userProfile.id}`);
                const hierarchyData = hierarchyResponse.data.data;
        
        // Use hierarchy names directly from backend response
        userProfile.businessHierarchy = hierarchyData.business_hierarchy_id ? {
          id: hierarchyData.business_hierarchy_id,
          name: hierarchyData.business_hierarchy_name
        } as any : null;
        
        userProfile.functionalHierarchy = hierarchyData.functional_hierarchy_id ? {
          id: hierarchyData.functional_hierarchy_id,
          name: hierarchyData.functional_hierarchy_name
        } as any : null;
        
        userProfile.geographicHierarchy = hierarchyData.geographic_hierarchy_id ? {
          id: hierarchyData.geographic_hierarchy_id,
          name: hierarchyData.geographic_hierarchy_name
        } as any : null;
      } catch (error) {
        console.warn('Failed to fetch hierarchy information:', error);
      }
      
      // Fetch user roles
      try {
                        const rolesResponse = await api.get(`/users/${userProfile.id}/roles`);
                userProfile.roles = rolesResponse.data.data || [];
      } catch (error) {
        console.warn('Failed to fetch user roles:', error);
        userProfile.roles = [];
      }
      
      // Convert relative image URL to full URL if needed
      if (userProfile.profileImageUrl) {
        if (!userProfile.profileImageUrl.startsWith('http')) {
          // It's a relative path - construct full URL
          const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
          // Ensure path starts with / but doesn't have double slashes
          const imagePath = userProfile.profileImageUrl.startsWith('/') 
            ? userProfile.profileImageUrl 
            : `/${userProfile.profileImageUrl}`;
          // URL encode each path segment to handle spaces and special characters (but keep slashes)
          const pathParts = imagePath.split('/');
          const encodedParts = pathParts.map((part: string) => part ? encodeURIComponent(part) : '');
          const encodedPath = encodedParts.join('/');
          userProfile.profileImageUrl = `${apiUrl}${encodedPath}`;
        }
      }
      
      setProfile(userProfile);
      setSelectedPositionId((userProfile as any).primaryPositionId || '');

      // Load tenant positions for dropdown (requires auth)
      try {
        const posRes = await api.get('/business-positions/positions');
        if (posRes.data?.positions) {
          setPositionOptions(posRes.data.positions);
        }
      } catch (_) {
        setPositionOptions([]);
      }
      
      // Map old theme values to new ones
      const mapOldThemeToNew = (oldTheme: string) => {
        const themeMap: Record<string, string> = {
          'light': 'tmos-light',
          'dark': 'midnight-slate',
          'auto': 'tmos-light'
        };
        return themeMap[oldTheme] || oldTheme || 'tmos-light';
      };

      const initialFormData = {
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        phone: userProfile.phone || '',
        title: userProfile.title || '',
        themePreference: mapOldThemeToNew(userProfile.preferences?.theme),
        language: userProfile.preferences?.language || 'en-US',
        timezone: userProfile.preferences?.timezone || 'UTC',
        dateFormat: userProfile.preferences?.dateFormat || 'MM/DD/YYYY',
        timeFormat: userProfile.preferences?.timeFormat || '12h',
        currency: userProfile.preferences?.currency || 'USD',
        notifications: {
          email: userProfile.preferences?.notifications_email === 'true',
          push: userProfile.preferences?.notifications_push === 'true',
          sms: userProfile.preferences?.notifications_sms === 'true',
          inApp: userProfile.preferences?.notifications_inApp !== 'false'
        },
        dashboardLayout: userProfile.preferences?.dashboardLayout || 'grid',
        compactMode: userProfile.preferences?.compactMode === 'true',
        autoSave: userProfile.preferences?.autoSave !== 'false',
        accessibility: {
          highContrast: userProfile.preferences?.accessibility_highContrast === 'true',
          fontSize: userProfile.preferences?.accessibility_fontSize || 'medium',
          screenReader: userProfile.preferences?.accessibility_screenReader === 'true'
        },
        privacy: {
          profileVisibility: userProfile.preferences?.privacy_profileVisibility || 'team',
          activityStatus: userProfile.preferences?.privacy_activityStatus || 'visible',
          dataSharing: userProfile.preferences?.privacy_dataSharing || 'minimal'
        }
      };

      setFormData(initialFormData);

      // Initialize hierarchy form data
      setHierarchyFormData({
        business_hierarchy_id: userProfile.businessHierarchy?.id || '',
        functional_hierarchy_id: userProfile.functionalHierarchy?.id || '',
        geographic_hierarchy_id: userProfile.geographicHierarchy?.id || ''
      });

      // Load theme from user preferences
      const userTheme = mapOldThemeToNew(userProfile.preferences?.theme);
      loadThemeFromPreferences(userTheme);
      
      // Load language from user preferences
      const userLanguage = userProfile.preferences?.language || 'en-US';
                        loadLanguageFromPreferences(userLanguage);
          } catch (error: any) {
            console.error('17. Error type:', typeof error);
      console.error('18. Error message:', error.message);
      console.error('19. Error response exists:', !!error.response);
      
      if (error.response) {
        console.error('20. Response status:', error.response.status);
        console.error('21. Response status text:', error.response.statusText);
        console.error('22. Response data:', error.response.data);
        console.error('23. Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('24. Request was made but no response received');
        console.error('25. Request details:', error.request);
      } else {
        console.error('26. Error setting up request:', error.message);
      }
      
      console.error('27. Full error object:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
                    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: checked
        };
        // Auto-save after state update
        setTimeout(() => autoSaveField(name, checked), 100);
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        // Auto-save after state update
        setTimeout(() => autoSaveField(name, value), 100);
        return newData;
      });
    }
    
      };

  // Removed handleInputBlur since we now save immediately on change

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
                
    // Handle nested accessibility font size field
    if (name === 'accessibility_fontSize') {
      setFormData(prev => {
        const newData = {
          ...prev,
          accessibility: {
            ...prev.accessibility,
            fontSize: value
          }
        };
        // Auto-save after state update
        setTimeout(() => autoSaveField('accessibility_fontSize', value), 100);
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        // Debounced auto-save after state update
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }
        const timeout = setTimeout(() => autoSaveField(name, value), 500);
        setAutoSaveTimeout(timeout);
        return newData;
      });
    }
    
    // Special handling for theme changes - apply immediately
    if (name === 'themePreference') {
            setTheme(value);
    }
    
    // Special handling for language changes - apply immediately
    if (name === 'language') {
            setLanguage(value);
    }
    
      };

  const handleNotificationChange = (type: string, checked: boolean) => {
                
    setFormData(prev => {
      const newData = {
        ...prev,
        notifications: {
          ...prev.notifications,
          [type]: checked
        }
      };
              // Debounced auto-save after state update
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }
        const timeout = setTimeout(() => autoSaveField(`notifications_${type}`, checked), 500);
        setAutoSaveTimeout(timeout);
      return newData;
    });
    
      };

  // const handleThemeChange = (themeId: string) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     themePreference: themeId
  //   }));
  //   setTheme(themeId);
  //   // Debounced auto-save theme preference
  //   if (autoSaveTimeout) {
  //     clearTimeout(autoSaveTimeout);
  //   }
  //   const timeout = setTimeout(() => autoSaveField('themePreference', themeId), 500);
  //   setAutoSaveTimeout(timeout);
  // };

  const handleAccessibilityChange = (setting: string, value: string | boolean) => {
                
    setFormData(prev => {
      const newData = {
        ...prev,
        accessibility: {
          ...prev.accessibility,
          [setting]: value
        }
      };
              // Debounced auto-save after state update
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }
        const timeout = setTimeout(() => autoSaveField(`accessibility_${setting}`, value), 500);
        setAutoSaveTimeout(timeout);
      return newData;
    });
    
      };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPictureError(t('profile.selectImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPictureError(t('profile.imageSizeTooLarge'));
      return;
    }

    setUploadingPicture(true);
    setPictureError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile_picture');

      const response = await api.post('/users/upload-profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.data?.profileImageUrl) {
        const newProfileImageUrl = response.data.data.profileImageUrl;
        
        // Build full URL if it's a relative path (development)
        // The path from backend is like /uploads/filename.jpg, and we serve it at /uploads
        let fullImageUrl = newProfileImageUrl;
        if (!newProfileImageUrl.startsWith('http')) {
          const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
          // Ensure path starts with / but doesn't have double slashes
          const imagePath = newProfileImageUrl.startsWith('/') 
            ? newProfileImageUrl 
            : `/${newProfileImageUrl}`;
          // URL encode each path segment to handle spaces and special characters (but keep slashes)
          const pathParts = imagePath.split('/');
          const encodedParts = pathParts.map((part: string) => part ? encodeURIComponent(part) : '');
          const encodedPath = encodedParts.join('/');
          fullImageUrl = `${apiUrl}${encodedPath}`;
        }
        
        // Update profile with new image URL IMMEDIATELY
        setProfile(prev => prev ? {
          ...prev,
          profileImageUrl: fullImageUrl
        } : null);
        
        // Update user in auth context IMMEDIATELY so navbar shows the new picture
        if (user) {
          setUser({
            ...user,
            profileImageUrl: fullImageUrl
          });
        }
        
        // Refetch profile to ensure everything is in sync (but don't wait for it)
        fetchUserProfile().catch(err => console.error('Failed to refetch profile:', err));
        
        // Also refresh user data from backend
        try {
          const { authService } = await import('@/services/auth');
          const updatedUser = await authService.getCurrentUser();
          if (updatedUser) {
            // Convert profileImageUrl if needed
            if (updatedUser.profileImageUrl && !updatedUser.profileImageUrl.startsWith('http')) {
              const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
              const imagePath = updatedUser.profileImageUrl.startsWith('/') 
                ? updatedUser.profileImageUrl 
                : `/${updatedUser.profileImageUrl}`;
              updatedUser.profileImageUrl = `${apiUrl}${imagePath}`;
            }
            setUser(updatedUser);
          }
        } catch (error) {
          console.error('Failed to refresh user data:', error);
        }
      }
    } catch (error: any) {
      setPictureError(error.response?.data?.message || t('profile.uploadFailed'));
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError(t('profile.passwordAllFieldsRequired'));
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError(t('auth.passwordTooShort'));
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('auth.passwordsDoNotMatch'));
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await api.post('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.data.success) {
        setPasswordSuccess(t('profile.passwordChanged'));
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowChangePassword(false);
          setPasswordSuccess('');
        }, 2000);
      }
    } catch (error: any) {
      setPasswordError(error.response?.data?.message || t('profile.passwordChangeFailed'));
    } finally {
      setChangingPassword(false);
    }
  };

  // const handlePrivacyChange = (setting: string, value: string) => {
  //     //     //       
  //   setFormData(prev => {
  //     const newData = {
  //       ...prev,
  //       privacy: {
  //         ...prev.privacy,
  //         [setting]: value
  //       }
  //     };
  //             // Debounced auto-save after state update
  //         if (autoSaveTimeout) {
  //           clearTimeout(autoSaveTimeout);
  //         }
  //         const timeout = setTimeout(() => autoSaveField(`privacy_${setting}`, value), 500);
  //         setAutoSaveTimeout(timeout);
  //       return newData;
  //     });
    
  //       //   };

  const autoSaveField = async (fieldName: string, fieldValue: any) => {
                        
    if (autoSaving || !profile) {
            return;
    }
    
        setAutoSaving(true);
    setError('');
    
    try {
            
      // Always include current firstName and lastName to satisfy backend requirements
      const requestData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      };
      
      // Build request data based on field type (title removed - use Position instead)
      if (['firstName', 'lastName', 'phone'].includes(fieldName)) {
        // Direct user fields - update the specific field
        requestData[fieldName] = fieldValue;
      } else if (['themePreference', 'language', 'timezone', 'dateFormat', 'timeFormat', 'currency', 'dashboardLayout'].includes(fieldName)) {
        // Direct preference fields
        requestData.preferences = { [fieldName === 'themePreference' ? 'theme' : fieldName]: fieldValue };
      } else if (fieldName.startsWith('notifications_')) {
        // Notification fields
        const notificationType = fieldName.replace('notifications_', '');
        requestData.preferences = { [`notifications_${notificationType}`]: fieldValue.toString() };
      } else if (fieldName.startsWith('accessibility_')) {
        // Accessibility fields
        const accessibilitySetting = fieldName.replace('accessibility_', '');
        requestData.preferences = { [`accessibility_${accessibilitySetting}`]: fieldValue.toString() };
      } else if (fieldName.startsWith('privacy_')) {
        // Privacy fields
        const privacySetting = fieldName.replace('privacy_', '');
        requestData.preferences = { [`privacy_${privacySetting}`]: fieldValue };
      } else if (['compactMode', 'autoSave'].includes(fieldName)) {
        // Boolean preference fields
        requestData.preferences = { [fieldName]: fieldValue.toString() };
      }

      await api.put('/users/profile', requestData);
      
      // Update profile state with the specific field
      setProfile(prev => prev ? {
        ...prev,
        ...(requestData.firstName && { firstName: requestData.firstName }),
        ...(requestData.lastName && { lastName: requestData.lastName }),
        ...(requestData.phone && { phone: requestData.phone }),
        ...(requestData.preferences && {
          preferences: {
            ...prev.preferences,
            ...requestData.preferences
          }
        })
      } : null);

      // Special handling for theme and language
      if (fieldName === 'themePreference' && fieldValue) {
        localStorage.setItem('tmos-theme', fieldValue);
      }
      
      if (fieldName === 'language' && fieldValue) {
        localStorage.setItem('tmos-language', fieldValue);
      }
      
            
    } catch (error: any) {
      console.error('=== FIELD AUTO-SAVE ERROR ===');
      console.error('1. Error type:', typeof error);
      console.error('2. Error message:', error.message);
      console.error('3. Error response exists:', !!error.response);
      
      if (error.response) {
        console.error('4. Response status:', error.response.status);
        console.error('5. Response status text:', error.response.statusText);
        console.error('6. Response data:', error.response.data);
        console.error('7. Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('8. Request was made but no response received');
        console.error('9. Request details:', error.request);
      } else {
        console.error('10. Error setting up request:', error.message);
      }
      
      console.error('11. Full error object:', error);
      setError(`Auto-save failed for ${fieldName}: ${error.response?.data?.message || error.message}`);
    } finally {
      setAutoSaving(false);
    }
  };

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setSaving(true);
  //   setMessage('');
  //   setError('');

  //   try {
  //     const requestData = {
  //       firstName: formData.firstName,
  //       lastName: formData.lastName,
  //       phone: formData.phone,
  //       preferences: {
  //         theme: formData.themePreference,
  //         language: formData.language,
  //         timezone: formData.timezone,
  //         dateFormat: formData.dateFormat,
  //         timeFormat: formData.timeFormat,
  //         currency: formData.currency,
  //         notifications_email: formData.notifications.email.toString(),
  //         notifications_push: formData.notifications.push.toString(),
  //         notifications_sms: formData.notifications.sms.toString(),
  //         notifications_inApp: formData.notifications.inApp.toString(),
  //         dashboardLayout: formData.dashboardLayout,
  //         compactMode: formData.compactMode.toString(),
  //         autoSave: formData.autoSave.toString(),
  //         accessibility_highContrast: formData.accessibility.highContrast.toString(),
  //         accessibility_fontSize: formData.accessibility.fontSize,
  //         accessibility_screenReader: formData.accessibility.screenReader.toString(),
  //         privacy_profileVisibility: formData.privacy.profileVisibility,
  //         privacy_activityStatus: formData.privacy.activityStatus,
  //         privacy_dataSharing: formData.privacy.dataSharing
  //       }
  //     };

  //     const response = await api.put('/users/profile', requestData);
  //     setMessage('Profile updated successfully!');
      
  //     // Update the profile state with new data
  //     if (profile) {
  //       setProfile({
  //         ...profile,
  //         firstName: formData.firstName,
  //         lastName: formData.lastName,
  //         phone: formData.phone,
  //         preferences: {
  //           ...profile.preferences,
  //           theme: formData.themePreference
  //         },
  //         updatedAt: response.data.data.updatedAt
  //       });
  //     }
  //   } catch (error: any) {
  //     console.error('Error updating profile:', error);
  //     setError(error.response?.data?.message || 'Failed to update profile');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-info)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[var(--color-error)]">{t('profile.failedToLoadProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[var(--color-surface-text)] mb-1">{t('profile.title')}</h1>
          <p className="text-sm text-[var(--color-secondary-text)]">{t('profile.description')}</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-[var(--color-border)]">
          <nav className="-mb-px flex flex-wrap gap-4 text-sm">
            {[
              { id: 'personal', label: t('navigation.personalInformation') || 'Personal information' },
              { id: 'business', label: t('navigation.businessInformation') || 'Business information' },
              { id: 'subscription', label: t('navigation.subscription') || 'Subscription' },
              { id: 'preferences', label: t('navigation.preferences') || 'Preferences' },
            ].map((tab) => {
              const isActive = activeSection === tab.id;
              const onClick = () => {
                setSearchParams({ section: tab.id });
              };
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={onClick}
                  className={`px-3 py-2 border-b-2 ${
                    isActive
                      ? 'border-[#004C94] text-[#004C94]'
                      : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)] hover:border-[var(--color-border)]'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Section 1: Personal Information */}
          <div className={activeSection === 'personal' ? '' : 'hidden'}>
            <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
                <div className="space-y-4">
                  {/* Profile Picture */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="relative">
                      {profile?.profileImageUrl ? (
                        <img
                          src={profile.profileImageUrl}
                          alt={`${profile.firstName} ${profile.lastName}`}
                          className="w-20 h-20 rounded-full object-cover border-2 border-[var(--color-border)]"
                          onError={() => {
                            console.error('Image failed to load:', profile.profileImageUrl);
                          }}
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] flex items-center justify-center text-2xl font-medium border-2 border-[var(--color-border)]">
                          {profile ? `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() : 'U'}
                        </div>
                      )}
                      {uploadingPicture && (
                        <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.profilePicture')}
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        className="hidden"
                        disabled={uploadingPicture}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPicture}
                        className="px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {uploadingPicture
                          ? t('profile.uploading')
                          : profile?.profileImageUrl
                          ? t('profile.changePicture')
                          : t('profile.uploadPicture')}
                      </button>
                      {pictureError && (
                        <p className="text-xs text-[var(--color-error)] mt-1">{pictureError}</p>
                      )}
                      <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                        {t('profile.imageUploadHelp')}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                      {t('auth.email')}
                    </label>
                    <Input
                      type="email"
                      value={profile.email}
                      disabled
                      className="bg-[var(--color-surface-bg)] border-[var(--color-border)] text-[var(--color-surface-text)]"
                    />
                    <p className="text-xs text-[var(--color-secondary-text)] mt-1">{t('profile.emailCannotBeChanged')}</p>
                    
                    {/* Change Password Link */}
                    <div className="mt-2">
                      {!showChangePassword ? (
                        <button
                          onClick={() => setShowChangePassword(true)}
                          className="text-sm text-[var(--color-info)] hover:underline"
                        >
                          {t('profile.changePassword')}
                        </button>
                      ) : (
                        <form onSubmit={handleChangePassword} className="space-y-3 mt-3">
                          {passwordError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                              {passwordError}
                            </div>
                          )}
                          
                          {passwordSuccess && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
                              {passwordSuccess}
                            </div>
                          )}
                          
                          <div>
                            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                              {t('profile.currentPasswordLabel')}
                            </label>
                            <Input
                              type="password"
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                              required
                              placeholder={t('profile.enterCurrentPassword')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                              {t('profile.newPasswordLabel')}
                            </label>
                            <Input
                              type="password"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                              required
                              placeholder={t('auth.passwordPlaceholder')}
                              minLength={8}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                              {t('profile.confirmNewPasswordLabel')}
                            </label>
                            <Input
                              type="password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                              required
                              placeholder={t('profile.confirmNewPasswordPlaceholder')}
                              minLength={8}
                            />
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <button
                              type="submit"
                              disabled={changingPassword}
                              className="px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {changingPassword
                                ? t('profile.changingPassword')
                                : t('profile.changePassword')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowChangePassword(false);
                                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                                setPasswordError('');
                                setPasswordSuccess('');
                              }}
                              className="px-4 py-2 border border-[var(--color-border)] text-[var(--color-surface-text)] rounded-md hover:bg-[var(--color-secondary-bg)]"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.firstName')} *
                      </label>
                      <Input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        placeholder={t('profile.enterFirstName')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.lastName')} *
                      </label>
                      <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        placeholder={t('profile.enterLastName')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.phone')}
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t('profile.enterPhoneNumber')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.hierarchyScope') || 'Hierarchy scope'}
                      </label>
                      <Input
                        type="text"
                        value={
                          profile.hasTenantRootMembership
                            ? (t('profile.hierarchyScope.fullTenant') || 'Full tenant (root member)')
                            : (t('profile.hierarchyScope.scoped') || 'Scoped (department/project only)')
                        }
                        disabled
                        className="bg-[var(--color-surface-bg)] border-[var(--color-border)] text-[var(--color-surface-text)]"
                      />
                      <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                        {t('profile.hierarchyScope.help') ||
                          'Full-tenant scope means you are attached to the tenant root and can see the full hierarchy (subject to clearance).'}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Section 2: Business Information */}
            <div className={activeSection === 'business' ? '' : 'hidden'}>
              <Card className="p-6 bg-[var(--color-surface-bg)] border-0 shadow-none">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.companyCannotBeChanged')}
                      </label>
                      <Input
                        type="text"
                        value={profile.tenantName}
                        disabled
                        className="bg-[var(--color-surface-bg)] border-[var(--color-border)] text-[var(--color-surface-text)]"
                      />
                    </div>

                    <div ref={positionDropdownRef}>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.position') || 'Position'}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={positionDropdownOpen ? positionSearch : (positionOptions.find(p => p.id === selectedPositionId)?.position_name || '')}
                          onChange={(e) => {
                            setPositionSearch(e.target.value);
                            setPositionDropdownOpen(true);
                          }}
                          onFocus={() => {
                            setPositionDropdownOpen(true);
                            if (!positionSearch && selectedPositionId) {
                              const name = positionOptions.find(p => p.id === selectedPositionId)?.position_name || '';
                              setPositionSearch(name);
                            }
                          }}
                          placeholder={t('profile.selectPosition') || 'Select or type to search position...'}
                          className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-info)] focus:border-[var(--color-info)]"
                        />
                        {positionDropdownOpen && (
                          <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg">
                            {positionOptions
                              .filter(p => !positionSearch.trim() || p.position_name.toLowerCase().includes(positionSearch.toLowerCase()) || (p.position_code && p.position_code.toLowerCase().includes(positionSearch.toLowerCase())))
                              .map((p) => (
                                <li
                                  key={p.id}
                                  className="px-3 py-2 cursor-pointer hover:bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                                  onClick={async () => {
                                    setSelectedPositionId(p.id);
                                    setPositionSearch('');
                                    setPositionDropdownOpen(false);
                                    setSavingPosition(true);
                                    try {
                                      await api.put('/users/profile/position', { positionId: p.id });
                                      if (profile) setProfile({ ...profile, title: p.position_name, primaryPositionId: p.id });
                                    } catch (err) {
                                      console.error('Failed to update position', err);
                                    } finally {
                                      setSavingPosition(false);
                                    }
                                  }}
                                >
                                  <span className="font-medium">{p.position_name}</span>
                                  {p.position_code && <span className="text-[var(--color-secondary-text)] ml-2 text-sm">{p.position_code}</span>}
                                </li>
                              ))}
                            {positionOptions.filter(p => !positionSearch.trim() || p.position_name.toLowerCase().includes(positionSearch.toLowerCase()) || (p.position_code && p.position_code.toLowerCase().includes(positionSearch.toLowerCase()))).length === 0 && (
                              <li className="px-3 py-2 text-[var(--color-secondary-text)]">{t('profile.noPositions') || 'No positions found'}</li>
                            )}
                          </ul>
                        )}
                      </div>
                      {savingPosition && <p className="text-xs text-[var(--color-secondary-text)] mt-1">{t('profile.saving') || 'Saving...'}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('profile.clearance') || 'Clearance'}
                      </label>
                      <Input
                        type="text"
                        value={profile.clearance ?? 'Internal'}
                        disabled
                        className="bg-[var(--color-surface-bg)] border-[var(--color-border)] text-[var(--color-surface-text)]"
                        title={t('profile.clearanceReadOnly') || 'Clearance can only be changed by an administrator.'}
                      />
                      <p className="text-xs text-[var(--color-secondary-text)] mt-1">{t('profile.clearanceReadOnly') || 'Clearance can only be changed by an administrator.'}</p>
                    </div>
                  </div>
                  
                  {/* Organizational Hierarchy */}
                  <div>
                    <h3 className="text-md font-medium text-[var(--color-surface-text)] mb-3">
                      {t('profile.organizationalHierarchy')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                          {t('profile.businessHierarchy')}
                        </label>
                        <select
                          value={hierarchyFormData.business_hierarchy_id}
                          onChange={(e) => {
                            setHierarchyFormData(prev => ({ ...prev, business_hierarchy_id: e.target.value }));
                            // Debounce save to avoid multiple rapid saves
                            if (hierarchySaveTimeout) {
                              clearTimeout(hierarchySaveTimeout);
                            }
                            const timeout = setTimeout(() => saveHierarchies(), 1000);
                            setHierarchySaveTimeout(timeout);
                          }}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={savingHierarchies}
                        >
                        <option value="">{t('profile.notAssigned')}</option>
                          {businessHierarchyOptions.map((hierarchy) => (
                            <option key={hierarchy.id} value={hierarchy.id}>
                              {hierarchy.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                          {t('profile.functionHierarchy')}
                        </label>
                        <select
                          value={hierarchyFormData.functional_hierarchy_id}
                          onChange={(e) => {
                            setHierarchyFormData(prev => ({ ...prev, functional_hierarchy_id: e.target.value }));
                            // Debounce save to avoid multiple rapid saves
                            if (hierarchySaveTimeout) {
                              clearTimeout(hierarchySaveTimeout);
                            }
                            const timeout = setTimeout(() => saveHierarchies(), 1000);
                            setHierarchySaveTimeout(timeout);
                          }}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={savingHierarchies}
                        >
                          <option value="">{t('profile.notAssigned')}</option>
                          {functionalHierarchyOptions.map((hierarchy) => (
                            <option key={hierarchy.id} value={hierarchy.id}>
                              {hierarchy.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                          {t('profile.geographyHierarchy')}
                        </label>
                        <select
                          value={hierarchyFormData.geographic_hierarchy_id}
                          onChange={(e) => {
                            setHierarchyFormData(prev => ({ ...prev, geographic_hierarchy_id: e.target.value }));
                            // Debounce save to avoid multiple rapid saves
                            if (hierarchySaveTimeout) {
                              clearTimeout(hierarchySaveTimeout);
                            }
                            const timeout = setTimeout(() => saveHierarchies(), 1000);
                            setHierarchySaveTimeout(timeout);
                          }}
                          className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={savingHierarchies}
                        >
                          <option value="">{t('profile.notAssigned')}</option>
                          {geographicHierarchyOptions.map((hierarchy) => (
                            <option key={hierarchy.id} value={hierarchy.id}>
                              {hierarchy.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {savingHierarchies && (
                      <p className="text-sm text-blue-600 mt-2">
                        {t('profile.savingHierarchies')}
                      </p>
                    )}
                  </div>
                  
                  {/* User Roles */}
                  <div>
                    <h3 className="text-md font-medium text-[var(--color-surface-text)] mb-3">
                      {t('profile.userRoles')}
                    </h3>
                    <div className="space-y-3">
                      {profile.roles && profile.roles.length > 0 ? (
                        profile.roles.map((role) => (
                          <div key={role.id} className="flex items-center justify-between p-3 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md">
                            <div>
                              <div className="font-medium text-[var(--color-surface-text)]">{role.name}</div>
                              {role.description && (
                                <div className="text-sm text-[var(--color-secondary-text)]">{role.description}</div>
                              )}
                            </div>
                            <div className="w-2 h-2 bg-[var(--color-success)] rounded-full"></div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-6">
                          <div className="text-[var(--color-secondary-text)]">
                            {t('profile.noRoles')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Subscription Section (embedded) */}
            <div className={activeSection === 'subscription' ? '' : 'hidden'}>
              <Card className="p-4 bg-[var(--color-surface-bg)] border-0 shadow-none">
                <TenantSubscriptionPage
                  embedded
                  initialAttemptId={searchParams.get('attemptId') || undefined}
                  initialPlanId={searchParams.get('planId') || undefined}
                  onPlanAttemptConsumed={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('attemptId');
                      next.delete('planId');
                      return next;
                    });
                  }}
                />
              </Card>
            </div>

            {/* Preferences Section */}
            <PreferencesTab
              active={activeSection === 'preferences'}
              formData={formData}
              availableThemes={availableThemes}
              availableLanguages={availableLanguages}
              handleSelectChange={handleSelectChange}
              handleNotificationChange={handleNotificationChange}
              handleAccessibilityChange={handleAccessibilityChange}
              handleInputChange={handleInputChange}
            />

          </div>
      </div>
    </div>
  );
};
